/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import type { ChangeEvent } from "react";
import {
  styButton,
  styFormItem,
  styModalConfirmGift,
  styModalConfirmGiftContent,
  styMoodalConfirmGiftTitle,
} from "./styles";
import Button from "../Button";
import { ACCOUNT_INFO, WEDDING_GIFT_CONFIRMATION_URL } from "../../constants";
import { Lang } from "../../context/LocaleContext";
import {
  txtCancelConfirmation,
  txtConfirmationTitle,
  txtInputAccount,
  txtInputNominal,
  txtInputSenderPlaceholder,
  txtOkConfirmation,
  txtOkConfirming,
} from "./locales";

type ModalConfirmGiftProps = {
  isOpen: boolean;
  lang?: Lang;
  onClose: () => void;
};

const ModalConfirmGift = ({
  isOpen,
  lang = "id",
  onClose,
}: ModalConfirmGiftProps) => {
  const [name, setName] = useState("");
  const [selectedBank, setSelectedBank] = useState<string>("");
  const [nominal, setNominal] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleBankChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSelectedBank(event.target.value);
  };

  const handleNominalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (/^[0-9]*$/.test(value)) {
      setNominal(value);
    }
  };

  const handleResetData = () => {
    setName("");
    setSelectedBank("");
    setNominal("");
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    formData.append("Name", name);
    formData.append("Destination", selectedBank);
    formData.append("Nominal", nominal);

    setSubmitting(true);

    try {
      const response = await fetch(WEDDING_GIFT_CONFIRMATION_URL, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        console.log("Data inserted successfully");
        onClose();
        handleResetData();
      } else {
        console.error("Failed to insert data");
      }
    } catch (error) {
      console.error("Error inserting data:", error);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    // Add event listener to disable scrolling when the modal is open
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    // Clean up the event listener on component unmount
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div css={styModalConfirmGift}>
      <div css={styModalConfirmGiftContent}>
        <h2 css={styMoodalConfirmGiftTitle}>{txtConfirmationTitle[lang]}</h2>
        <div css={styFormItem}>
          <input
            type="text"
            id="name"
            value={name}
            onChange={handleNameChange}
            placeholder={txtInputSenderPlaceholder[lang]}
          />
        </div>
        <div css={styFormItem}>
          <select
            id="bank"
            value={selectedBank ?? ""}
            onChange={handleBankChange}
          >
            <option value="" disabled>
              {txtInputAccount[lang]}
            </option>
            {ACCOUNT_INFO.map(({ name, bank }) => (
              <option key={bank} value={bank}>
                {bank} a.n {name}
              </option>
            ))}
          </select>
        </div>
        <div css={styFormItem}>
          <input
            type="number"
            id="nominal"
            value={nominal}
            onChange={handleNominalChange}
            placeholder={txtInputNominal[lang]}
          />
        </div>
        <div css={styFormItem}>
          <Button
            customStyles={styButton}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? txtOkConfirming[lang] : txtOkConfirmation[lang]}
          </Button>
          <Button
            customStyles={styButton}
            onClick={onClose}
            disabled={submitting}
          >
            {txtCancelConfirmation[lang]}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ModalConfirmGift;
